export type Track =
  | "foundation"
  | "discovery"
  | "adoption"
  | "compliance"
  | "scale";

export type WizardOption = {
  label: string;
  tracks: Partial<Record<Track, number>>;
};

export type WizardQuestion = {
  id: string;
  question: string;
  options: WizardOption[];
};

export type Recommendation = {
  track: Track;
  headline: string;
  body: string;
  relatedCases: string[]; // case slugs
  cta: { label: string; to: string };
};

export const wizardQuestions: WizardQuestion[] = [
  {
    id: "stage",
    question: "Where are you on the platform journey?",
    options: [
      { label: "Just exploring the idea", tracks: { foundation: 2 } },
      { label: "Building our first version", tracks: { foundation: 1, adoption: 1 } },
      { label: "We have a platform, but adoption is low", tracks: { discovery: 2 } },
      { label: "Scaling across many teams", tracks: { scale: 2 } },
    ],
  },
  {
    id: "pain",
    question: "What's the biggest pain right now?",
    options: [
      { label: "Starting a new project takes weeks", tracks: { foundation: 1, adoption: 1 } },
      { label: "Developers don't use what we built", tracks: { discovery: 2, adoption: 1 } },
      { label: "Compliance is a slow, manual gate", tracks: { compliance: 2 } },
      { label: "No visibility into the platform", tracks: { scale: 1, adoption: 1 } },
    ],
  },
  {
    id: "contribution",
    question: "How is contribution handled today?",
    options: [
      { label: "Only a central team builds anything", tracks: { adoption: 2 } },
      { label: "A few teams contribute now and then", tracks: { adoption: 1, scale: 1 } },
      { label: "We have a strong inner-source culture", tracks: { scale: 2 } },
      { label: "We haven't thought about it yet", tracks: { foundation: 2 } },
    ],
  },
  {
    id: "outcome",
    question: "Which outcome matters most?",
    options: [
      { label: "A faster path to production", tracks: { foundation: 1, scale: 1 } },
      { label: "Higher adoption and ownership", tracks: { adoption: 2, discovery: 1 } },
      { label: "Compliance and security by default", tracks: { compliance: 2 } },
      { label: "Measurable cost & efficiency gains", tracks: { scale: 1, adoption: 1 } },
    ],
  },
];

export const recommendations: Record<Track, Recommendation> = {
  foundation: {
    track: "foundation",
    headline: "Start with discovery, not tooling",
    body: "You're early — and that's exactly where the biggest mistakes get locked in. We start every engagement by discovering real developer pain points through structured, time-boxed workshops, so the platform you build removes friction instead of adding another tool. We've done this from day one at IKEA.",
    relatedCases: ["ikea-it"],
    cta: { label: "Talk to us about discovery", to: "/contact" },
  },
  discovery: {
    track: "discovery",
    headline: "You built it. Now let's make them come.",
    body: "A platform with low adoption isn't a technical problem — it's a friction problem. We embed with your teams, experience the friction ourselves, and map where work actually gets stuck. At Volvo Group this turned 421 page views a month into 13,860.",
    relatedCases: ["volvo-group"],
    cta: { label: "Talk to us about adoption", to: "/contact" },
  },
  adoption: {
    track: "adoption",
    headline: "Make the platform theirs, not yours",
    body: "When a single team builds everything, the platform stalls. The shift happens when developers can contribute, improve and shape the tools they use every day. At Volvo Group, 70% of all templates are now contributed by teams outside the core team — the platform stopped being our project and became theirs. We build the inner-source contribution model that gets you there.",
    relatedCases: ["volvo-group", "ikea-it"],
    cta: { label: "Talk to us about contribution", to: "/contact" },
  },
  compliance: {
    track: "compliance",
    headline: "Compliance by default, not by audit",
    body: "Short feedback loops built into the developer workflow turn compliance from a periodic gate into a continuous signal — surfaced early and cheap, not late and expensive. In our experience it's also one of the strongest incentives for developers to onboard. At Länsförsäkringar we're building exactly this on top of Red Hat Developer Hub and OpenShift.",
    relatedCases: ["lansforsakringar"],
    cta: { label: "Talk to us about compliance", to: "/contact" },
  },
  scale: {
    track: "scale",
    headline: "Scaling is an organisational challenge",
    body: "At scale, the hard part isn't the technology — it's the delivery and contribution model that lets hundreds of teams build on the platform without chaos. We've learned this driving adoption at IKEA (~1,900 weekly users, 60%+ increased usage) and across hundreds of teams at Volvo Group, and we bring short feedback loops and a single developer-facing view of every signal.",
    relatedCases: ["ikea-it", "volvo-group"],
    cta: { label: "Talk to us about scaling", to: "/contact" },
  },
};

export function resolveRecommendation(
  selections: WizardOption[],
): Recommendation {
  const scores: Record<Track, number> = {
    foundation: 0,
    discovery: 0,
    adoption: 0,
    compliance: 0,
    scale: 0,
  };

  selections.forEach((option) => {
    (Object.entries(option.tracks) as [Track, number][]).forEach(
      ([track, weight]) => {
        scores[track] += weight;
      },
    );
  });

  const winner = (Object.entries(scores) as [Track, number][]).reduce(
    (best, current) => (current[1] > best[1] ? current : best),
  );

  return recommendations[winner[0]];
}
